import { cx } from '@flowus/common/cx';
import { useEffect, type FC, type MouseEvent } from 'react';
import { Icon } from 'src/common/components/icon';
import { useOpenModal } from 'src/common/components/next-modal';
import { Tooltip } from 'src/common/components/tooltip';
import { usePermissions } from 'src/hooks/share/use-permissions';
import { useGetPageId } from 'src/utils/getPageId';
import { usePickBlock } from 'src/utils/pick-block';
import { bindDataTestId, TestIds } from 'src/utils/qa-utils';
import { useIsLogin } from 'src/hooks/user/use-is-login';
import { WikiList } from 'src/views/main/aside/wiki/wiki-list';
import { WikiManager } from 'src/redux/managers/block/wiki-manager';
import { usePersonalWikiId } from 'src/views/main/aside/wiki/use-personal-wiki-id';
import { transaction } from 'src/hooks/use-transaction';
import { setBlockPermission } from 'src/redux/managers/block/update-permission';
import { PermissionRole, PermissionType } from '@next-space/fe-api-idl';
import { getRolePermissionRole } from 'src/hooks/share/use-permission-utils';
import { message } from '@flowus/common/components/message';
import { useApiState } from '@flowus/common/hooks/use-api-state';
import { request } from 'src/common/request';

interface Props {
  uuid?: string;
  className?: string;
}

export const WikiButton: FC<Props> = ({ uuid, className }) => {
  const openModal = useOpenModal();
  const pageId = useGetPageId();
  const id = uuid || pageId;
  const block = usePickBlock(id, ['permissions']);
  const isLogin = useIsLogin();
  const { shared, role, allowSubscribe } = usePermissions(id);
  const personalWikiId = usePersonalWikiId();

  if (!block) return null;
  if (!isLogin) return null;
  if (!allowSubscribe) return null;

  const openMenu = async (event: MouseEvent<HTMLElement>) => {
    openModal.dropdown({
      popcorn: event.currentTarget,
      content: ({ onCloseModal }) => (
        <WikiList
          pageId={id}
          onDimiss={() => {
            onCloseModal();
          }}
          onSelectWiki={(wikiId) => {
            onCloseModal();

            if (WikiManager.addReferencePage(wikiId, pageId)) {
              if (wikiId !== personalWikiId) {
                const { editor } = getRolePermissionRole(role);
                if (!shared && editor) {
                  transaction(() => {
                    setBlockPermission(id, {
                      role: PermissionRole.READER,
                      type: PermissionType.PUBLIC,
                    });
                  });
                }
              }
              message.success('已添加到知识库');
            } else {
              WikiManager.removeReferencePage(wikiId, pageId);
              message.success('已从知识库移除');
            }
          }}
        />
      ),
    });
  };

  return (
    <div
      {...bindDataTestId(TestIds.driveFileShare)}
      onClick={openMenu}
      className={cx('flex items-center justify-center rounded animate-hover p-1', className)}
    >
      <Tooltip popup="添加到知识库">
        <Icon size="middle" name="IcKnowledgeBase" />
      </Tooltip>
    </div>
  );
};
